import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faPencil,
  faTrash,
  faImage,
  faVideo,
  faFile,
  faNoteSticky,
  faShield,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import type { Finding, InspectionFindingSeverity } from '@/services/inspection.service';
import { FindingPhotoStrip } from './FindingPhotoStrip';
import { VideoSection } from './VideoSection';
import { DocumentSection } from './DocumentSection';
import { FindingNotesSection } from './FindingNotesSection';
import { AuthorizationSection } from './AuthorizationSection';

// Status color system (PART 4): red = critical, amber = warning, green = low.
// No orange — HIGH/MEDIUM share the amber "caution" family; the label + the
// left accent below distinguish them.
const SEVERITY_BADGE: Record<InspectionFindingSeverity, string> = {
  CRITICAL: 'bg-red-500/10 text-red-700',
  HIGH: 'bg-amber-500/10 text-amber-700',
  MEDIUM: 'bg-amber-500/10 text-amber-700',
  LOW: 'bg-green-500/10 text-green-700',
};

// PART 3: each finding stays a light card with a left colored accent by severity.
const SEVERITY_ACCENT: Record<InspectionFindingSeverity, string> = {
  CRITICAL: 'border-l-red-500',
  HIGH: 'border-l-amber-500',
  MEDIUM: 'border-l-amber-400',
  LOW: 'border-l-green-500',
};

const SEVERITY_LABELS: Record<InspectionFindingSeverity, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

const AUTH_STATUS_BADGE: Record<string, string> = {
  PENDING: 'text-amber-600',
  APPROVED: 'text-green-700',
  REJECTED: 'text-red-700',
  EXPIRED: 'text-muted-foreground',
};

const AUTH_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Auth: Pending',
  APPROVED: 'Auth: Approved',
  REJECTED: 'Auth: Rejected',
  EXPIRED: 'Auth: Expired',
};

function fmtTime(v: string): string {
  return new Date(v).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  finding: Finding;
  inspectionId: string;
  canModify: boolean;
  canUploadAttachments: boolean;
  canDeleteMedia: boolean;
  canCreateFindingNote: boolean;
  canCreateAuthorization: boolean;
  canRespondAuthorization: boolean;
  isNewlyCreated?: boolean;
  onEdit: (finding: Finding) => void;
  onDelete: (findingId: string) => void;
}

export function FindingAccordionItem({
  finding,
  inspectionId,
  canModify,
  canUploadAttachments,
  canDeleteMedia,
  canCreateFindingNote,
  canCreateAuthorization,
  canRespondAuthorization,
  isNewlyCreated = false,
  onEdit,
  onDelete,
}: Props) {
  const [isOpen, setIsOpen] = useState(isNewlyCreated);
  const [hasBeenOpened, setHasBeenOpened] = useState(isNewlyCreated);

  const handleToggle = () => {
    if (!hasBeenOpened) setHasBeenOpened(true);
    setIsOpen((p) => !p);
  };

  const hasEvidence = finding.photoCount > 0 || finding.videoCount > 0 || finding.documentCount > 0;
  const createdUpdatedSame = finding.createdAt === finding.updatedAt;

  return (
    <div
      className={`overflow-hidden rounded-lg border border-l-4 border-border bg-card ${SEVERITY_ACCENT[finding.severity]}`}
    >
      {/* Accordion header */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-start gap-3 px-4 py-3 text-left bg-muted/10 hover:bg-muted/30 transition min-h-[48px]"
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
        />

        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Row 1: number · title · severity · flags */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-mono text-muted-foreground shrink-0">
              #{finding.findingNumber}
            </span>
            <span className="text-sm font-semibold text-foreground">{finding.title}</span>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${SEVERITY_BADGE[finding.severity]}`}
            >
              {SEVERITY_LABELS[finding.severity]}
            </span>
            {finding.componentArea && (
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                {finding.componentArea}
              </span>
            )}
            {finding.insideRequestedScope && (
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs text-primary">
                In Scope
              </span>
            )}
            {finding.warrantyApplicable && (
              <span className="rounded-full bg-green-500/10 px-2.5 py-0.5 text-xs text-green-700">
                Warranty
              </span>
            )}
            {finding.isCommercialReview && (
              <span className="rounded-full bg-blue-500/10 px-2.5 py-0.5 text-xs text-blue-700">
                Commercial
              </span>
            )}
          </div>

          {/* Row 2: evidence counts · notes · auth status · timestamps */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {/* Evidence */}
            {hasEvidence && (
              <span className="flex items-center gap-2">
                {finding.photoCount > 0 && (
                  <span className="flex items-center gap-1">
                    <FontAwesomeIcon icon={faImage} className="h-3 w-3" />
                    {finding.photoCount}
                  </span>
                )}
                {finding.videoCount > 0 && (
                  <span className="flex items-center gap-1">
                    <FontAwesomeIcon icon={faVideo} className="h-3 w-3" />
                    {finding.videoCount}
                  </span>
                )}
                {finding.documentCount > 0 && (
                  <span className="flex items-center gap-1">
                    <FontAwesomeIcon icon={faFile} className="h-3 w-3" />
                    {finding.documentCount}
                  </span>
                )}
              </span>
            )}

            {/* Notes */}
            {finding.noteCount > 0 && (
              <span className="flex items-center gap-1">
                <FontAwesomeIcon icon={faNoteSticky} className="h-3 w-3" />
                {finding.noteCount}
              </span>
            )}

            {/* Authorization */}
            {finding.authorizationStatus && (
              <span
                className={`flex items-center gap-1 ${AUTH_STATUS_BADGE[finding.authorizationStatus] ?? 'text-muted-foreground'}`}
              >
                <FontAwesomeIcon icon={faShield} className="h-3 w-3" />
                {AUTH_STATUS_LABEL[finding.authorizationStatus] ??
                  `Auth: ${finding.authorizationStatus}`}
              </span>
            )}

            {/* Timestamps */}
            <span className="flex items-center gap-1 ml-auto">
              <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
              {fmtTime(finding.createdAt)}
              {!createdUpdatedSame && (
                <span className="text-muted-foreground/60">· upd {fmtTime(finding.updatedAt)}</span>
              )}
            </span>
          </div>
        </div>

        {/* Edit / Delete — stopPropagation prevents accordion toggle */}
        {canModify && (
          <div
            className="flex shrink-0 items-center gap-1 ml-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => onEdit(finding)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Edit finding"
            >
              <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(finding.id)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              aria-label="Delete finding"
            >
              <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
            </button>
          </div>
        )}
      </button>

      {/* Expanded body — lazy: content mounts only after first open.
          CSS grid-rows 0fr→1fr animates to the content's exact height and
          collapses cleanly back to zero, so no reserved/blank space is left
          behind (replaces the old max-h-[9999px] hack). */}
      {hasBeenOpened && (
        <div
          className={`grid transition-[grid-template-rows] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
        >
          <div className="min-h-0 space-y-5 overflow-hidden p-5 bg-background">
            {/* Finding narrative — grouped and given room before the evidence panels */}
            <div className="space-y-3">
              <p className="text-sm leading-relaxed text-foreground">{finding.description}</p>

              {(finding.recommendedAction || finding.recommendedPartsSummary) && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {finding.recommendedAction && (
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">
                        Recommended Action
                      </span>
                      <p className="mt-0.5 text-sm leading-relaxed text-foreground">
                        {finding.recommendedAction}
                      </p>
                    </div>
                  )}
                  {finding.recommendedPartsSummary && (
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">
                        Recommended Parts
                      </span>
                      <p className="mt-0.5 text-sm leading-relaxed text-foreground">
                        {finding.recommendedPartsSummary}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {(finding.estimatedRepairHours || finding.berCategory) && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {finding.estimatedRepairHours && (
                    <span>Est. Repair: {finding.estimatedRepairHours} hrs</span>
                  )}
                  {finding.berCategory && <span>BER: {finding.berCategory}</span>}
                  {finding.berReason && <span>{finding.berReason}</span>}
                </div>
              )}
            </div>

            <FindingPhotoStrip
              inspectionId={inspectionId}
              findingId={finding.id}
              canUpload={canUploadAttachments}
              canDelete={canDeleteMedia}
            />

            <VideoSection
              inspectionId={inspectionId}
              findingId={finding.id}
              canUpload={canUploadAttachments}
              canDelete={canDeleteMedia}
            />

            <DocumentSection
              inspectionId={inspectionId}
              findingId={finding.id}
              canUpload={canUploadAttachments}
              canDelete={canDeleteMedia}
            />

            <FindingNotesSection
              inspectionId={inspectionId}
              findingId={finding.id}
              canCreate={canCreateFindingNote}
            />

            <AuthorizationSection
              inspectionId={inspectionId}
              findingId={finding.id}
              canCreate={canCreateAuthorization}
              canRespond={canRespondAuthorization}
            />
          </div>
        </div>
      )}
    </div>
  );
}
